import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { requestProductDetails } from 'src/redux/product/thunks';
import { adaptCatServiceAPIResponseForStore } from 'src/redux/product/utils/adaptCatServiceAPIResponseForStore';
import { getClientLogger } from 'src/utils/clientLogger';
import { getUpdatedSelectedOptionValues, UpdateExisting } from './helpers/sliceHelpers';
import {
  addItemToBasket,
  addMembershipItemToBasket,
  getBasketForViewing,
  populateOptionValuesFromBasketItem,
  updateItemInBasket,
} from './thunks';
import type {
  BasketState,
  ImageUploadAttemptPayload,
  ImageUploadErrorPayload,
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  ProgressTrackingEvent,
  RequiredImageOptionImpressionPayload,
  SelectedOptionValuesRecord,
  SelectionPayload,
  SetOptionsExpandedPayload,
} from './types';

export { type BasketState } from './types';

export const initialState: Readonly<BasketState> = {
  basket: {},
  trackingData: null,
  error: null,
  isPending: false,
  editedItem: null,
  isBasketModalOpen: false,
  isQuantityValid: true,
  quantity: 1,
  options: [],
  imageNotFoundUrlsByOption: {},
  selectedOptionValues: {} as SelectedOptionValuesRecord,
  isOptionsExpanded: false,
  hasAttemptedSubmit: false,
  trackedEvents: {
    focus: {},
    progress: {
      'Progress - 50%': false,
      'Progress - 100%': false,
    },
    error: {
      'Character Limit Error': {},
      'Add to Cart Error': {},
    },
  },
};

export const { actions, reducer } = createSlice({
  name: 'basket',
  initialState,
  reducers: {
    clearBasketError(state) {
      state.error = null;
    },
    openBasketModal: (state) => {
      state.isBasketModalOpen = true;
    },
    closeBasketModal: (state) => {
      state.isBasketModalOpen = false;
    },
    changeQuantity: (state, action: PayloadAction<{ isValid: boolean; quantity: number }>) => {
      state.quantity = action.payload.quantity;
      state.isQuantityValid = action.payload.isValid;
    },
    showValidationErrors: (state) => {
      state.hasAttemptedSubmit = true;
    },
    deselectOption: (state, { payload }: PayloadAction<SelectionPayload>) => {
      delete state.selectedOptionValues[payload.optionId];
    },
    selectOption: (state, { payload }: PayloadAction<SelectionPayload>) => {
      state.selectedOptionValues = getUpdatedSelectedOptionValues(
        state.selectedOptionValues,
        payload,
        UpdateExisting.ChangeInPlace,
      );
    },
    toggleSelectOption: (state, { payload }: PayloadAction<SelectionPayload>) => {
      state.selectedOptionValues = getUpdatedSelectedOptionValues(
        state.selectedOptionValues,
        payload,
        UpdateExisting.Dedupe,
      );
    },
    setOptionsExpanded: (state, { payload }: PayloadAction<SetOptionsExpandedPayload>) => {
      state.isOptionsExpanded = payload.expanded;
    },
    trackInteraction: (state, { payload }: PayloadAction<InteractionTrackingEvent>) => {
      if (payload.field_action === 'Field Focus') {
        state.trackedEvents.focus[payload.field_name] = true;
      } else {
        state.trackedEvents.progress[payload.field_name] = true;
      }
    },
    trackProgressMilestone: (state, { payload }: PayloadAction<ProgressTrackingEvent>) => {
      state.trackedEvents.progress[payload.field_action] = true;
      state.trackedEvents.progress[payload.field_name] = true;
    },
    trackInteractionError: (state, { payload }: PayloadAction<InteractionErrorTrackingEvent>) => {
      state.trackedEvents.error[payload.field_action][payload.field_name] = true;
    },
    // Pure tracking actions: no state mutation, the GA events map intercepts
    // these to dispatch sendGAEvent. Kept on the slice so they share the
    // same dispatch surface as the rest of the personalisation analytics.
    trackRequiredImageOptionImpression: (
      _state,
      _action: PayloadAction<RequiredImageOptionImpressionPayload>,
    ) => {},
    trackImageUploadError: (_state, _action: PayloadAction<ImageUploadErrorPayload>) => {},
    trackImageUploadAttempt: (_state, _action: PayloadAction<ImageUploadAttemptPayload>) => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestProductDetails.fulfilled, (state, { payload }) => {
        if (payload.data) {
          state.options = adaptCatServiceAPIResponseForStore(payload.data).options;
        }
      })
      .addCase(populateOptionValuesFromBasketItem.fulfilled, (state, { payload }) => {
        const { discountedItemsTotal, item } = payload;

        state.basket.discountedItemsTotal = discountedItemsTotal;

        // No matching item means the `editItem` id didn't resolve to a real
        // basket item, so we're not actually editing anything — leave edit
        // mode off and the form in its default (add) state.
        if (!item) {
          return;
        }

        state.editedItem = item.id;
        state.quantity = item.quantity;

        // The basket item is the source of truth for the edited item's
        // selections, so rebuild from scratch. Without this reset, a repeat
        // dispatch (StrictMode double-invoke, re-entering edit mode) would
        // push duplicate values onto non-image options and leave stale keys
        // from a previous hydration in place.
        state.selectedOptionValues = {};

        for (const option of item.options) {
          const optionKey = option.optionId as unknown as number;
          if (!state.selectedOptionValues[optionKey]) {
            state.selectedOptionValues[optionKey] = [];
          }

          const basketType = option.type?.toUpperCase();

          if (basketType === 'IMAGE') {
            state.selectedOptionValues[optionKey] = [
              {
                customOptionKind: 'image',
                id: Number(option.optionId),
                imagePreviewUrls: option.imagePreviewUrls
                  ? [...option.imagePreviewUrls]
                  : undefined,
                isCustomInput: true,
                name: option.value ?? '',
                priceModifier: null,
              },
            ];
            continue;
          }

          state.selectedOptionValues[optionKey]?.push({
            id: parseInt(String(option.valueId || option.optionId), 10),
            name: option.value || option.valueLabel!,
            priceModifier: null,
            isCustomInput: !!option.value,
            isValid: true,
          });
        }
      })
      .addCase(getBasketForViewing.fulfilled, (state, { payload }) => {
        state.basket.discountedItemsTotal = payload.discountedItemsTotal;
        state.basket.itemsTotal = payload.itemsTotal;
        state.basket.membershipAddedStatus = payload.membershipAddedStatus;
      })
      .addCase(addItemToBasket.pending, (state) => {
        state.error = null;
        state.imageNotFoundUrlsByOption = {};
        state.isPending = true;
      })
      .addCase(addItemToBasket.fulfilled, (state, { payload }) => {
        const { basket, trackingData } = payload;

        state.basket.discountedItemsTotal = basket.discountedItemsTotal;
        state.basket.itemsCount = basket.itemsCount;
        state.basket.itemsTotal = basket.itemsTotal;
        state.trackingData = trackingData;
        state.error = null;
        state.isPending = false;
      })
      .addCase(addItemToBasket.rejected, (state, action) => {
        const map = action.payload?.imageNotFoundUrlsByOption;
        if (map && Object.keys(map).length > 0) {
          state.imageNotFoundUrlsByOption = map;
          state.error = null;
        } else {
          getClientLogger().error(`the addItemToBasket action failed: ${action.error.message}`);
          state.error = action.error;
        }
        state.isPending = false;
      })
      .addCase(updateItemInBasket.pending, (state) => {
        state.error = null;
        state.imageNotFoundUrlsByOption = {};
        state.isPending = true;
      })
      // No `fulfilled` case: a successful update redirects to the basket page
      // (see updateItemInBasket thunk), so any state written here would be
      // discarded. Leaving `isPending` true keeps the CTA in its "Saving" state
      // through the navigation.
      .addCase(updateItemInBasket.rejected, (state, action) => {
        const map = action.payload?.imageNotFoundUrlsByOption;
        if (map && Object.keys(map).length > 0) {
          state.imageNotFoundUrlsByOption = map;
          state.error = null;
        } else {
          getClientLogger().error(`the updateItemInBasket action failed: ${action.error.message}`);
          state.error = action.error;
        }
        state.isPending = false;
      })
      .addCase(addMembershipItemToBasket.pending, (state) => {
        state.error = null;
        state.isPending = true;
      })
      // TODO: refactor to avoid code duplication with addItemToBasket and updateItemInBasket
      .addCase(addMembershipItemToBasket.fulfilled, (state, { payload }) => {
        const { basket } = payload;

        state.basket.discountedItemsTotal = basket.discountedItemsTotal;
        state.basket.itemsCount = basket.itemsCount;
        state.basket.itemsTotal = basket.itemsTotal;
        state.basket.membershipAddedStatus = payload.basket.membershipAddedStatus;
        state.error = null;
        state.isPending = false;
      })
      .addCase(addMembershipItemToBasket.rejected, (state, action) => {
        getClientLogger().error(
          `the addMembershipItemToBasket action failed: ${action.error.message}`,
        );

        state.error = action.error;
        state.isPending = false;
      });
  },
});
